import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CommentModerationFilter,
  ImageModerationMode,
  ImageModerationTab,
  ModerationActions,
  ModerationTier,
  getCommentsToModerate,
  performModerationActionOnComment,
  getBuildingNotes
} from '~/api/admin';
import { useFullScreenLoader } from '~/hooks/useFullScreenLoader';
import {
  CommentModerationAction,
  CommentSourceType,
  ContentStatus,
  FlaggedComment,
  getColumnsForModerationTable,
  ModerationNotesLink
} from './tableColumns';
import { withFullScreenLoader } from '~/hocs/withFullScreenLoader';
import GenericModal from '~/components/GenericModal/GenericModal';
import ModerationCommentDetails from './CommentDetails';
import { Colors } from '~/enums/Colors';
import './style.css';
import { Tabs } from '~/components/Tabs';
import CobuTableAdvanced from '~/components/CobuTableAdvanced/CobuTableAdvanced';
import { ApiState } from '~/types/common';
import {
  moveCommentToMarketplace,
  updateGroupComment,
  updateGroupEventComment
} from '~/api/comment';
import { toast } from 'react-toastify';
import { CocResult, evaluateCommentAgainstCoC } from '~/api/openai';
import CobuSelect from '~/components/CobuSelect/CobuSelect';
import { SelectType } from '~/enums/SelectType';
import { SelectedOptionType } from '~/types/form';
import { ImageModerationPanel } from '~/containers/ImageModerationContainer';

// IMAGES (AUTO MOD) sits last: it switches the row shape to one photo per row,
// while every other tab is one comment per row over the same fetched array.
const filtersList = [
  {
    label: 'ALL',
    value: 'all'
  },
  {
    label: 'FLAGGED BY USER',
    value: 'flagged'
  },
  {
    label: 'HIDDEN POSTS',
    value: 'hidden'
  },
  {
    label: 'MARKETPLACE',
    value: 'marketplace'
  },
  {
    // Marketplace v2 only: listings and listing comments. They also show under MARKETPLACE.
    label: 'LISTINGS',
    value: 'listings'
  },
  {
    label: 'IMAGES',
    value: 'containsImages'
  },
  {
    label: 'IMAGES (AUTO MOD)',
    value: 'autoModImages'
  }
];

// Per-image queues from `/admin/moderation/images`. These act on one photo, not
// a whole comment.
const IMAGE_SUB_TABS: { label: string; value: ImageModerationTab }[] = [
  { label: 'NEEDS REVIEW', value: 'review' },
  { label: 'AUTO-HIDDEN', value: 'hidden' }
];

// Scopes every filter tab below, comments and images alike.
const TIER_TABS: { label: string; value: ModerationTier }[] = [
  { label: 'PAID BUILDINGS', value: 'paid' },
  { label: 'FREE BUILDINGS', value: 'free' }
];

const sortOptions = [
  {
    label: 'Chronology',
    value: 'chronology'
  },
  {
    label: 'Building',
    value: 'building'
  },
  {
    label: 'Sentiment',
    value: 'sentiment'
  }
];

const directionOptions = [
  {
    label: 'Descending',
    value: 'desc'
  },
  {
    label: 'Ascending',
    value: 'asc'
  }
];

// Server caps pageSize at 100 (MAX_PAGE_SIZE in comments-to-moderate.js).
const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50, 100];

const ContentModerationContainer = () => {
  // One page, already filtered, sorted and paged by the server.
  const [flaggedComments, setFlaggedComments] = useState<FlaggedComment[]>([]);
  const [commentToExpand, setCommentToExpand] = useState<FlaggedComment>();
  const { loader } = useFullScreenLoader();
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [getCommentsApiState, setgetCommentsApiState] = useState<ApiState>('LOADING');
  const [sortOption, setSortOption] = useState<string>('chronology');
  const [sortDirection, setSortDirection] = useState<string>('desc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [counts, setCounts] = useState<Partial<Record<CommentModerationFilter, number>>>({});

  // Move the building notes state to the component level
  const [buildingNotes, setBuildingNotes] = useState<{[key: string]: any[]}>({});
  const [cocResults, setCocResults] = useState<Record<string, CocResult>>({});
  const [cocModalUuid, setCocModalUuid] = useState<string | null>(null);
  const [cocLoading, setCocLoading] = useState<Record<string, boolean>>({});

  const [imageSubTab, setImageSubTab] = useState<ImageModerationTab>('review');

  const [tier, setTier] = useState<ModerationTier>('paid');
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const requestId = useRef(0);

  // From the comments fetch. Assume enforce until it answers, so the legacy
  // IMAGES tab does not flash in.
  const [moderationMode, setModerationMode] =
    useState<ImageModerationMode>('enforce');

  const normalizeCommentFlags = (comment: any) => ({
    ...comment,
    // Some payloads may omit this field or send non-boolean values.
    // Normalize so table actions can reliably render MARK/UNMARK state.
    greatContent: Boolean(comment?.greatContent),
    isPublicFacing: Boolean(comment?.isPublicFacing)
  });

  // Fetch building notes when comments change
  useEffect(() => {
    const loadBuildingNotes = async () => {
      try {
        // Fix: Convert the Set to Array before using it
        const buildingIds = Array.from(new Set(flaggedComments.map(comment => comment.buildingId)));
        const notesPromises = buildingIds.map(buildingId => getBuildingNotes(buildingId));

        const notesResults = await Promise.all(notesPromises);
        const notesMap = buildingIds.reduce((acc, buildingId, index) => {
          acc[buildingId] = notesResults[index].data?.rows?.filter((row: { pinned: boolean; note: string }) => row.pinned).map((row: { note: string }) => row.note) || [];
          return acc;
        }, {} as {[key: string]: any[]});
        setBuildingNotes(notesMap);
      } catch (error) {
      }
    };

    // Load building notes independently of flagged comments
    loadBuildingNotes();
  }, [flaggedComments]); // Empty dependency array to only run on mount

  const filteredComments = useMemo(
    () =>
      flaggedComments.map((comment) => ({
        ...comment,
        buildingNotes: buildingNotes[comment.buildingId] || []
      })),
    [flaggedComments, buildingNotes]
  );

  // The auto-mod tab renders its own image queue; the comment fetch behind it
  // only supplies imageModerationMode, so it reads the ALL tab.
  const commentFilter = (
    selectedFilter === 'autoModImages' ? 'all' : selectedFilter
  ) as CommentModerationFilter;

  const getComments = useCallback(() => {
    // Responses can land out of order, so only the newest may write state — a
    // late FREE response would otherwise populate the table under a PAID tab.
    const id = ++requestId.current;
    loader.show();
    setgetCommentsApiState('LOADING');

    return getCommentsToModerate({
      tier,
      filter: commentFilter,
      sort: sortOption,
      direction: sortDirection,
      page,
      pageSize
    })
      .then((res) => {
        if (id !== requestId.current) return;
        const comments = (res.data?.comments || []).map(normalizeCommentFlags);
        setFlaggedComments(comments);
        setTotal(res.data?.total ?? 0);
        setTotalPages(res.data?.totalPages ?? 0);
        setCounts(res.data?.counts ?? {});
        if (res.data?.imageModerationMode) {
          setModerationMode(res.data.imageModerationMode);
        }
      })
      // Clear, or the previous page's rows stay actionable under the new page number.
      .catch(() => {
        if (id !== requestId.current) return;
        setFlaggedComments([]);
        toast.error('Failed to load comments');
      })
      .finally(() => {
        if (id !== requestId.current) return;
        setgetCommentsApiState('COMPLETED');
        setHasLoadedOnce(true);
        loader.hide();
      });
  }, [tier, commentFilter, sortOption, sortDirection, page, pageSize]);

  useEffect(() => {
    getComments();
  }, [getComments]);

  // Acting on the last rows of the last page can leave `page` past the end.
  useEffect(() => {
    if (totalPages > 0 && page > totalPages - 1) setPage(totalPages - 1);
  }, [page, totalPages]);

  const performAction = ({
    comments,
    action,
    visibility
  }: {
    comments: FlaggedComment[];
    action: ModerationActions;
    visibility?: ContentStatus;
  }) => {
    loader.show();

    // A batch (REVIEW ABOVE) can span several comment types (group, place,
    // listing, listing comment) — the backend routes by a single commentType
    // per request, so split by type.
    const idsByType = comments.reduce((acc, comment) => {
      if (!acc[comment.commentType]) acc[comment.commentType] = [];
      acc[comment.commentType].push(comment.uuid);
      return acc;
    }, {} as Record<CommentSourceType, string[]>);

    Promise.all(
      Object.entries(idsByType).map(([commentType, id]) =>
        performModerationActionOnComment({
          id,
          visibility,
          action,
          commentType: commentType as CommentSourceType
        })
      )
    )
      // Refetch rather than splice — acting on a page shifts every later row up.
      .then(() => getComments())
      .finally(() => {
        loader.hide();
        setCommentToExpand(undefined);
      });
  };

  const pinComment = async (comment: FlaggedComment) => {
    try {
      loader.show();

      await updateGroupComment({ uuid: comment.uuid, pinComment: true });
      await performAction({ comments: [comment], action: 'MARK_REVIEWED' });

      toast.success('Comment pinned');
    } catch (error) {
      console.error(error);
    }

    loader.hide();
  };

  const moveToMarketplace = async (comment: FlaggedComment) => {
    try {
      loader.show();

      await moveCommentToMarketplace({
        building: {
          uuid: comment.buildingId
        },
        uuid: comment.uuid
      });

      toast.success('Comment moved to marketplace');
      await performAction({ comments: [comment], action: 'MARK_REVIEWED' });
    } catch (error) {
      console.error(error);
    }

    loader.hide();
  };

  const toggleGreatContent = async (comment: FlaggedComment) => {
    const payload = {
      uuid: comment.uuid,
      greatContent: !comment.greatContent
    };

    try {
      loader.show();

      // Moderation can include both group and gathering comments.
      // Try group comment endpoint first, then fallback to gathering comment endpoint.
      try {
        await updateGroupComment(payload);
      } catch (error) {
        await updateGroupEventComment(payload);
      }

      setFlaggedComments((prev) =>
        prev.map((item) =>
          item.uuid === comment.uuid
            ? { ...item, greatContent: !item.greatContent }
            : item
        )
      );
      toast.success(
        payload.greatContent
          ? 'Comment marked as Great Content'
          : 'Comment unmarked as Great Content'
      );
    } catch (error) {
      console.error(error);
      toast.error('Failed to update Great Content status');
    } finally {
      loader.hide();
    }
  };

  // GroupComment only — PlaceComment and GroupEventComment have no
  // isPublicFacing column, and the button is gated to groupComment rows.
  const togglePublicFacing = async (comment: FlaggedComment) => {
    const isPublicFacing = !comment.isPublicFacing;

    try {
      loader.show();
      await updateGroupComment({ uuid: comment.uuid, isPublicFacing });

      setFlaggedComments((prev) =>
        prev.map((item) =>
          item.uuid === comment.uuid ? { ...item, isPublicFacing } : item
        )
      );
      toast.success(
        isPublicFacing ? 'Comment added to website' : 'Comment removed from website'
      );
    } catch (error) {
      console.error(error);
      toast.error('Failed to update website visibility');
    } finally {
      loader.hide();
    }
  };

  const action = async (
    type: CommentModerationAction,
    comment: FlaggedComment
  ) => {
    switch (type) {
      case 'PIN_POST': {
        return pinComment(comment);
      }
      case 'MOVE_TO_MARKETPLACE': {
        return moveToMarketplace(comment);
      }
      case 'TOGGLE_GREAT_CONTENT': {
        return toggleGreatContent(comment);
      }
      case 'TOGGLE_PUBLIC_FACING': {
        return togglePublicFacing(comment);
      }
      case 'HIDE': {
        return changeCommentVisibility(comment, ContentStatus.HIDDEN);
      }
      case 'UNHIDE': {
        return changeCommentVisibility(comment, ContentStatus.VISIBLE);
      }
      case 'MARK_REVIEWED': {
        return markCommentReviewed(comment);
      }
    }
  };

  const markCommentReviewed = (comment: FlaggedComment) =>
    performAction({ comments: [comment], action: 'MARK_REVIEWED' });

  const changeCommentVisibility = (
    comment: FlaggedComment,
    visibility: ContentStatus
  ) =>
    performAction({
      comments: [comment],
      action: 'UPDATE_VISIBILITY',
      visibility
    });

  const reviewAboveComments = (selectedComment: FlaggedComment) => {
    const index = filteredComments.findIndex(
      (comment) => comment.uuid == selectedComment.uuid
    );

    const selectedComments = filteredComments.slice(0, index + 1);

    performAction({
      comments: selectedComments,
      action: 'MARK_REVIEWED'
    });
  };

  const evaluateCoC = async (comment: FlaggedComment) => {
    setCocLoading((prev) => ({ ...prev, [comment.uuid]: true }));
    try {
      const result = await evaluateCommentAgainstCoC(comment.content);
      setCocResults((prev) => ({ ...prev, [comment.uuid]: result }));
      toast.success(`CoC evaluation: ${result.status}`);
    } catch (error) {
      toast.error('CoC evaluation failed');
    } finally {
      setCocLoading((prev) => ({ ...prev, [comment.uuid]: false }));
    }
  };

  const displayCommentDetails = (comment: FlaggedComment) =>
    setCommentToExpand(comment);

  const closeCommentDetailsModal = () => setCommentToExpand(undefined);

  function mouseEnterOnRow(row: FlaggedComment) {
    document.getElementById(`reviewAboveButton-${row.uuid}`)!.style.visibility =
      'visible';
  }
  function mouseLeftRow(row: FlaggedComment) {
    document.getElementById(`reviewAboveButton-${row.uuid}`)!.style.visibility =
      'hidden';
  }

  const selectFilter = (value: string) => {
    // if (selectedFilter?.value == filter.value) {
    //   setSelectedFilter(undefined);
    // } else {
    //   setSelectedFilter(filter);
    // }
    if (!value) return;
    // Page 4 of the old tab may not exist under the new one.
    setPage(0);
    setSelectedFilter(value);
  };

  // Clear now rather than on the refetch, or the old tier's rows look like they
  // belong to the newly selected one.
  const selectTier = (value: string) => {
    if (!value || value === tier) return;
    setFlaggedComments([]);
    setPage(0);
    setTier(value as ModerationTier);
  };

  const handleSortChange = (selectedOption: SelectedOptionType | null) => {
    if (!selectedOption) return;
    setPage(0);
    setSortOption(selectedOption.value);
  };

  const handleDirectionChange = (selectedOption: SelectedOptionType | null) => {
    if (!selectedOption) return;
    setPage(0);
    setSortDirection(selectedOption.value);
  };

  const getCommentsCountByFilter = (value: string) => {
    return counts[value as CommentModerationFilter] ?? 0;
  };

  // Only on the first load. Bailing out on every LOADING would unmount the tier
  // tabs mid-switch, blanking the page under the click that caused it.
  if (getCommentsApiState == 'LOADING' && !hasLoadedOnce) return null;

  // Also gates the sort/order controls and row count, which describe comments.
  // The auto-mod tab is one photo per row, so the comment table is not it.
  const showCommentTable = selectedFilter !== 'autoModImages';

  // enforce: verdicts drive review, so the auto-mod queues replace the legacy
  // per-comment list. off: nothing scans, so the auto-mod queues are always
  // empty. shadow: both, since verdicts are recorded but not applied.
  const hiddenFilter: Record<ImageModerationMode, string | null> = {
    enforce: 'containsImages',
    off: 'autoModImages',
    shadow: null
  };
  const visibleFilters = filtersList.filter(
    (f) => f.value !== hiddenFilter[moderationMode]
  );

  return (
    <div style={{ padding: '24px' }}>
      <ModerationNotesLink />

      {/* Removing this block leaves the dashboard paid-only, the server default. */}
      <div className='flex justify-start margin-bottom-16'>
        <Tabs.Container onChange={selectTier} value={tier}>
          {TIER_TABS.map((tierTab) => (
            <Tabs.Option
              selected={tierTab.value === tier}
              key={tierTab.value}
              value={tierTab.value}
            >
              {tierTab.label}
            </Tabs.Option>
          ))}
        </Tabs.Container>
      </div>

      <div className='flex justify-start margin-bottom-16'>
        <Tabs.Container onChange={selectFilter} value={selectedFilter}>
          {visibleFilters.map((filter) => (
            <Tabs.Option
              selected={filter.value == selectedFilter}
              key={filter.value}
              value={filter.value}
            >
              {filter.label}
              {/* <div className='bullet-count margin-left-8'>
                {getCommentsCountByFilter(filter.value)}
              </div> */}
            </Tabs.Option>
          ))}
        </Tabs.Container>
      </div>

      {/* Sub-tabs, auto-mod only. Kept out of the row above, which is per-comment. */}
      {selectedFilter === 'autoModImages' && (
        <>
          <div className='flex justify-start margin-bottom-16'>
            <Tabs.Container
              onChange={(value) => value && setImageSubTab(value as ImageModerationTab)}
              value={imageSubTab}
            >
              {IMAGE_SUB_TABS.map((subTab) => (
                <Tabs.Option
                  key={subTab.value}
                  value={subTab.value}
                  selected={subTab.value === imageSubTab}
                >
                  {subTab.label}
                </Tabs.Option>
              ))}
            </Tabs.Container>
          </div>

          <ImageModerationPanel tab={imageSubTab} tier={tier} />
        </>
      )}

      {showCommentTable && (
      <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h4 style={{ color: Colors.cobuTextBlack, margin: 0 }}>
          Total comments - {total}
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px', color: Colors.cobuTextBlack }}>Sort by:</span>
            <div style={{ minWidth: '180px' }}>
              <CobuSelect
                options={sortOptions}
                onChange={handleSortChange}
                defaultValue={sortOptions.find(opt => opt.value === sortOption) || sortOptions[0]}
                selectType={SelectType.Form}
                isSearchable={false}
                placeholder="Sort by"
              />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px', color: Colors.cobuTextBlack }}>Order:</span>
            <div style={{ minWidth: '150px' }}>
            <CobuSelect
                options={directionOptions}
                onChange={handleDirectionChange}
                defaultValue={directionOptions.find(opt => opt.value === sortDirection) || directionOptions[0]}
                selectType={SelectType.Form}
                isSearchable={false}
                placeholder="Order"
              />
            </div>
          </div>
        </div>
      </div>
      <div id='comments-moderation-table margin-top-16'>
        <CobuTableAdvanced
          rowProps={{
            onMouseEnter: mouseEnterOnRow,
            onMouseLeave: mouseLeftRow
          }}
          columns={getColumnsForModerationTable({
            displayCommentDetails,
            onReviewAboveClicked: reviewAboveComments,
            action,
            evaluateCoC,
            cocResults,
            cocLoading,
            onShowCocReason: setCocModalUuid
          })}
          data={filteredComments}
          ignoreDefaultSortBehaviour
          noData={{ label: 'No comments found' }}
        ></CobuTableAdvanced>
      </div>
      {/* Same controls as CobuTable's built-in pager, driven by the server. Shown
          whenever there are rows, or a large page size could hide its own selector. */}
      {total > 0 && (
        <div style={{ marginBottom: '50px' }}>
          <div className='pagination'>
            <button onClick={() => setPage(0)} disabled={page === 0}>
              {'<<'}
            </button>
            <button onClick={() => setPage((p) => p - 1)} disabled={page === 0}>
              {'<'}
            </button>
            <button onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages - 1}>
              {'>'}
            </button>
            <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>
              {'>>'}
            </button>
            <span style={{ marginRight: '10px', marginLeft: '10px' }}>
              Page
              <strong>
                {page + 1} of {totalPages}
              </strong>
            </span>
          </div>
          <div className='pagination'>
            <span>
              Go to page:
              {/* Applied on Enter or blur, not per keystroke — each change is a fetch.
                  Keyed on page so it shows the current page after any navigation. */}
              <input
                key={page}
                type='number'
                min={1}
                max={totalPages}
                defaultValue={page + 1}
                onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                onBlur={(e) => {
                  // Whole pages only: the server rejects a fractional page.
                  const typed = Math.trunc(Number(e.target.value)) || 1;
                  const target = Math.min(Math.max(typed, 1), totalPages) - 1;
                  if (target !== page) setPage(target);
                  else e.target.value = String(page + 1);
                }}
                style={{
                  width: '100px',
                  marginRight: '10px',
                  marginLeft: '10px'
                }}
              />
            </span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPage(0);
                setPageSize(Number(e.target.value));
              }}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      </>
      )}
      <GenericModal
        title='Comment'
        showModal={!!commentToExpand}
        hideActionButton
        cancelOverride='Close'
        content={
          <ModerationCommentDetails
            comment={commentToExpand}
            onMarkReviewed={markCommentReviewed}
            onChangeVisibility={changeCommentVisibility}
          />
        }
        onChange={closeCommentDetailsModal}
        enableOverflowScrolls
      ></GenericModal>
      {cocModalUuid && (() => {
        const modalComment = flaggedComments.find((c) => c.uuid === cocModalUuid);
        const modalResult =
          cocResults[cocModalUuid] ??
          (modalComment?.cocVerdict
            ? { status: modalComment.cocVerdict, reasoning: modalComment.cocReasoning ?? '' }
            : undefined);
        if (!modalResult) return null;
        return (
          <GenericModal
            title='Code of Conduct Assessment'
            showModal
            hideActionButton
            cancelOverride='Close'
            content={
              <div style={{ padding: '8px 0' }}>
                <p style={{ fontWeight: 600, marginBottom: '8px' }}>
                  Assessment: {modalResult.status}
                </p>
                <p style={{ fontWeight: 600, marginBottom: '8px' }}>
                  Reasons (with references to the Code of Conduct)
                </p>
                <p style={{ lineHeight: 1.6 }}>{modalResult.reasoning}</p>
              </div>
            }
            onChange={() => setCocModalUuid(null)}
            enableOverflowScrolls
          />
        );
      })()}
    </div>
  );
};

export default withFullScreenLoader(ContentModerationContainer);
